import { GitRepoStructureDefinition } from '@y42/git';
import { waitFor } from '@y42/rxjs';
import { filter, firstValueFrom, from } from 'rxjs';
import { AbstractStoreModel } from '../models/abstract-store-model.model';
import { AbstractGitService, AbstractGitServiceConfig } from './state';
import { Y42GitFolder } from './wasm-git-folders.model';

export type AbstractFeatureGitServiceHydrationDefinition = Record<number, GitRepoStructureDefinition>;

export abstract class AbstractFeatureGitService<
  StoreModel extends AbstractStoreModel = AbstractStoreModel,
  Folder extends Y42GitFolder = Y42GitFolder,
> extends AbstractGitService<StoreModel, Folder> {
  constructor(
    protected readonly featureConfig: Omit<AbstractGitServiceConfig, 'parentFolders'> & {
      hydrationDefinition: AbstractFeatureGitServiceHydrationDefinition;
    },
  ) {
    super({ ...featureConfig, parentFolders: [] });
  }

  protected readonly errorHandlerService = this.featureConfig.errorHandlerService;

  async getAll() {
    const entities = await this.gitClient.hydrateStores({
      path: this.featureConfig.basePath,
      config: this.featureConfig.hydrationDefinition,
    });
    return entities;
  }

  async get($name: StoreModel['$name']) {
    const entities = await this.gitClient.hydrateStores({
      path: this.featureConfig.basePath,
      config: this.featureConfig.hydrationDefinition,
      entityNames: [$name],
    });
    return entities;
  }

  async pullChanges() {
    await this.gitClient.fetchChanges();
    await firstValueFrom(
      from(this.gitClient.pullChanges()).pipe(
        waitFor(this.gitQuery.gitStatusCode$.pipe(filter((status) => status === null))),
      ),
    );
  }
}
